/**
* @Author: Gang Jiang
* @Date: 2026-03-05 14:39:04
* @Last Modified by: Gang Jiang
* @Last Modified Time: 2026-06-05 16:30:00
* @Description: Database table: influencer_marketing.rpt_revenue_flow
*/

<template>
  <div class="payments-commission-detail" ref="zjdgx">
    <div class="bread-crumb">
      Payments<span @click="router.push('/payments/pending-payment')">Pending Payment</span><span
        class="current">Commission Details</span>
    </div>
    <h2>Payment</h2>

    <div class="summary-filter">
      <a-form :model="filter" class="filter-form">
        <a-row :gutter="[16, 16]" align="middle">
          <a-col :xs="24" :sm="12" :md="5">
            <a-form-item class="filter-item" label="Payment ID">
              <a-select v-model:value="filter.dmReceivedPaymentId" placeholder="Select"
                :options="paymentIds.map(id => ({ label: id.toString(), value: id }))" allowClear
                :getPopupContainer="triggerNode => triggerNode.parentNode" />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="6">
            <a-form-item class="filter-item" label="Affiliate">
              <a-select v-model:value="filter.affiliateId" placeholder="Select" allowClear :options="affiliateOptions"
                :getPopupContainer="(triggerNode) => triggerNode.parentNode" />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="7">
            <a-form-item class="filter-item" label="Payment Date">
              <a-range-picker v-model:value="filterDate" class="filter-date-picker" format="YYYY-MM-DD"
                :allowClear="true" :placeholder="['Start date', 'End date']" />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="24" :md="6" class="btns">
            <a-button type="primary" @click="handleSearch">Search</a-button>
            <a-button @click="handleReset">Reset</a-button>
            <a :href="exportUrl" download>
              <a-button type="primary">Export</a-button>
            </a>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <div class="commission-table">
      <a-table :data-source="commissionList" :columns="columns" :loading="isLoading" :scroll="{ x: 2600 }" size="small"
        :pagination="pagination" :showSorterTooltip="false" :row-class-name="commissionRowClassName">
        <template #headerCell="{ column }">
          <span>{{ column.key }}</span>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="
            column.dataIndex === 'eventTime' ||
            column.dataIndex === 'updateTime'
          ">
            {{
              record[column.dataIndex]
                ? moment(record[column.dataIndex]).format('YYYY-MM-DD HH:mm:ss')
                : '-'
            }}
          </template>
          <template v-else-if="column.dataIndex === 'commissionUsd' || column.dataIndex === 'originalCommission'">
            <span :class="record['commissionNotEqual'] ? 'red-mark' : ''">{{ record[column.dataIndex]}}</span>
          </template>
          <template v-else-if="column.key === 'Status'">
            <span :class="record.status?.replace(' ', '-').toLowerCase()">{{
              record.status
            }}</span>
          </template>
          <template v-else-if="column.key === 'Actions'">
            <a href="javascript:;" @click="setShowLogModalId(record.commissionId)">Log</a>
          </template>
          <template v-else>
            {{ record[column.dataIndex] }}
          </template>
        </template>
      </a-table>
    </div>

    <a-modal title="Log" class="log-modal" :width="1000" :footer="null" v-model:open="showLogModal"
      @cancel="hideLogModal" wrapClassName="log-modal-wrapper" :getContainer="$refs.zjdgx">
      <a-spin :spinning="logLoading">
        <a-table :data-source="logList" :columns="LogTableColumns" bordered :pagination="logPagination"
          :scroll="{ y: 480 }">
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'createTime'">
              {{ moment(record.createTime).format('YYYY-MM-DD HH:mm:ss') }}
            </template>
          </template>
        </a-table>
      </a-spin>
    </a-modal>
  </div>
</template>

<script setup>
import { message } from 'ant-design-vue'
import { ref, computed, onMounted, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import moment from 'moment'
import { apiDomain } from '../../../config'
import advertiser from '../../server/advertiser'
import { commissions, payments } from '../../server/index'
import { useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const paymentId = route.params.id

// 当前查看日志的 commissionId
const currentCommissionId = ref(null)

const filter = ref({
  influencerId: '',
  advertiserId: '',
  affiliateId: undefined,
  paymentDate: '',
  dmReceivedPaymentId: undefined,
})

const paymentIds = ref([])

const filterDate = ref([])

const affiliateOptions = ref([])

const commissionList = ref([])
const isLoading = ref(false)

const commissionRowClassName = (record) =>
  record?.commissionNotEqual ? 'commission-not-equal-row' : ''
const pagination = ref({
  current: 1,
  pageSize: 200,
  total: 0,
  showSizeChanger: false,
  onChange: (page, pageSize) => {
    pagination.value.current = page
    pagination.value.pageSize = pageSize
    fetchList()
  },
})

const exportUrl = computed(() => {
  const params = []

  if (filter.value.influencerId) {
    params.push(`influencerId=${filter.value.influencerId}`)
  }
  if (filter.value.advertiserId) {
    params.push(`advertiserId=${filter.value.advertiserId}`)
  }
  if (filter.value.affiliateId) {
    params.push(`affiliateId=${filter.value.affiliateId}`)
  }

  const query = params.length ? `?${params.join('&')}` : ''
  const base = `https://${apiDomain}/admin/influencer-marketing/v1/finance/kol/payment/${paymentId}/details/export`

  return `${base}${query}`
})

// log 相关
const logLoading = ref(false)
const showLogModal = ref(false)
const logList = ref([])
const LogTableColumns = [
  { title: 'Editor', dataIndex: 'editorName', key: 'editorName' },
  { title: 'Operation', dataIndex: 'operation', key: 'operation' },
  { title: 'Date', dataIndex: 'createTime', key: 'createTime' },
]
const logPagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: false,
  onChange: (page) => {
    getLog(page)
  },
})

const getLog = (page) => {
  if (!currentCommissionId.value) return

  logLoading.value = true
  commissions
    .getLogList({
      id: currentCommissionId.value,
      page,
      size: 10,
    })
    .then((res) => {
      logLoading.value = false

      if (res.success) {
        logList.value = res.data

        if (page === 1) {
          logPagination.value.total = res.total
        }

        logPagination.value.current = page
      } else {
        message.error(res.error)
        logLoading.value = false
      }
    })
}

const columns = [
  {
    dataIndex: 'paymentId',
    key: 'Payment ID',
    width: 110,
  },
  {
    dataIndex: 'affiliatePaymentId',
    key: 'Affiliate Payment ID',
    width: 150,
  },
  {
    dataIndex: 'status',
    key: 'Status',
    width: 90,
  },
  {
    dataIndex: 'type',
    key: 'Type',
    width: 120,
  },
  {
    dataIndex: 'commissionId',
    key: 'Commission ID',
    width: 120,
  },
  {
    dataIndex: 'eventTime',
    key: 'Event Time',
    width: 180,
  },
  {
    dataIndex: 'updateTime',
    key: 'Update Time',
    width: 180,
  },
  {
    dataIndex: 'commissionUsd',
    key: 'Commission USD',
    width: 150,
  },
  {
    dataIndex: 'commissionForKolUsd',
    key: 'Commission for KOL USD',
    width: 190,
  },
  {
    dataIndex: 'commissionForKolRatio',
    key: 'Commission for KOL Ratio',
    width: 200,
  },
  {
    dataIndex: 'commissionCurrency',
    key: 'Commission Currency',
    width: 180,
  },
  {
    dataIndex: 'originalCommission',
    key: 'Original Commission',
    width: 170,
  },
  {
    dataIndex: 'currencyRate',
    key: 'Currency Rate',
    width: 120,
  },
  {
    dataIndex: 'influencerUsername',
    key: 'Influencer Username',
    width: 160,
  },
  {
    dataIndex: 'influencerEmail',
    key: 'Influencer Email',
    width: 200,
  },
  {
    dataIndex: 'affiliate',
    key: 'Affiliate',
    width: 180,
  },
  {
    dataIndex: 'advertiser',
    key: 'Advertiser',
    width: 180,
  },
  {
    dataIndex: 'affiliateMid',
    key: 'Affiliate MID',
    width: 120,
  },
  {
    dataIndex: 'orderId',
    key: 'Order ID',
    width: 160,
  },
  {
    dataIndex: 'affiliateCommissionId',
    key: 'A.Commission ID',
    width: 140,
  },
  {
    dataIndex: 'itemId',
    key: 'Item ID(SKU)',
    width: 120,
  },
  {
    dataIndex: 'note',
    key: 'Note',
    width: 200,
  },
  {
    key: 'Actions',
    dataIndex: 'actions',
    width: 80,
    fixed: 'right',
  },
]

const fetchList = () => {
  const params = { 
    ...filter.value, 
    paymentId,
    type: 'pendingPayment',
    page: pagination.value.current,
    size: pagination.value.pageSize,
  };

  Object.keys(params).forEach((key) => {
    if (params[key] === null || params[key] === undefined || params[key] === '') {
      delete params[key]
    }
  })

  isLoading.value = true

  payments.getCommissionList(params).then((res) => {
    isLoading.value = false

    if (res.success) {
      commissionList.value = res.data
      pagination.value.total = res.total
    } else {
      message.error(res.message)
    }
  })
}

const handleSearch = () => {
  pagination.value.current = 1
  fetchList()
}

const handleReset = () => {
  filter.value.influencerId = ''
  filter.value.advertiserId = ''
  filter.value.affiliateId = undefined
  filter.value.dmReceivedPaymentId = undefined
  handleSearch()
}

const setShowLogModalId = (commissionId) => {
  currentCommissionId.value = commissionId
  showLogModal.value = true
  getLog(1)
}

const hideLogModal = () => {
  currentCommissionId.value = null
  showLogModal.value = false
}

onBeforeMount(() => {
  advertiser.getAffiliateList().then((res) => {
    affiliateOptions.value = (res?.data || []).map((item) => ({
      label: item.name,
      value: item.id,
    }))
  })
  payments.getPaymentIds(paymentId).then((res) => {
    paymentIds.value = res?.data || []
  })
})

onMounted(() => {
  fetchList()
})
</script>

<style lang="less" scoped>
.payments-commission-detail {
  .bread-crumb {
    margin-bottom: 4px;

    >span {
      cursor: pointer;
      margin-left: 25px;
      position: relative;

      &:before {
        top: -1px;
        left: -14px;
        content: '/';
        font-size: 14px;
        color: #999;
        position: absolute;
      }
    }
  }

  .summary-filter {
    margin: 12px 0;
    padding: 16px 20px;
    background: #fff;
    border-radius: 4px;

    .filter-form {
      .filter-item {
        margin-bottom: 0;
      }

      .btns {
        width: 100%;
        text-align: right;
        white-space: nowrap;

        .ant-btn {
          margin-left: 12px;
        }
      }

      @media (max-width: 768px) {
        .btns {
          text-align: left;
          white-space: normal;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;

          .ant-btn {
            margin-left: 0;
          }
        }
      }
    }
  }

  :deep(.commissions) {

    .bread-crumb,
    h2,
    .filter,
    .statistic {
      display: none;
    }

    .commission-table {
      margin-top: 0;
    }
  }

  .red-mark {
    color: #ff4d4f;
  }

  .commission-table :deep(tr.commission-not-equal-row > td) {
    background: #fce8e8;
  }

  /* Actions 固定列：不透明背景，遮挡横向滚动时后面的数据 */
  .commission-table :deep(.ant-table-thead > tr > th.ant-table-cell-fix-right) {
    background: #fafafa !important;
  }

  .commission-table :deep(.ant-table-tbody > tr > td.ant-table-cell-fix-right) {
    background: #fff !important;
  }

  .commission-table :deep(tr.commission-not-equal-row > td.ant-table-cell-fix-right) {
    background: #fce8e8 !important;
  }

  .commission-table :deep(.ant-table-tbody > tr.ant-table-row:hover > td.ant-table-cell-fix-right) {
    background: #fafafa !important;
  }

  .commission-table :deep(tr.commission-not-equal-row.ant-table-row:hover > td.ant-table-cell-fix-right) {
    background: #fce8e8 !important;
  }
}
</style>
